<template>
  <DeepDialog
    v-model="showModel"
    v-model:fullscreen="showAsFullscreen"
    :title="fileName"
    :max-width="1800"
    :class="showAsFullscreen ? 'my-0' : 'my-4'"
    :rounded="showAsFullscreen ? '0' : ''"
    content-class="h-100"
    card-text-cls="pa-0 d-flex flex-column overflow-hidden"
    :show-fullscreen="!fullscreen"
    @close="close()"
    @keydown="onKeydown"
  >
    <template #content>
      <v-hover v-slot="{ isHovering, props: hoverProps }">
        <div
          class="position-relative flex-grow-1 overflow-hidden"
          v-bind="hoverProps"
        >
          <FilePreview
            :src="fileSrc"
            :mime-type="fileMimeType"
            :filename="fileName"
            :page="initialPage"
            :loading="isLoadingItem"
          />
          <v-fade-transition>
            <FilePreviewNavButton
              v-if="totalCount > 1 && isHovering"
              direction="prev"
              :loading="isNavigatingPrev"
              @click="prev()"
            />
          </v-fade-transition>
          <v-fade-transition>
            <FilePreviewNavButton
              v-if="totalCount > 1 && isHovering"
              direction="next"
              :loading="isNavigatingNext"
              @click="next()"
            />
          </v-fade-transition>
        </div>
      </v-hover>
    </template>
  </DeepDialog>
</template>

<script setup lang="ts">
import { DeepDialog } from '@deepcloud/deep-ui-lib'
import FilePreview from './FilePreview.vue'
import FilePreviewNavButton from './FilePreviewNavButton.vue'

type PreviewItem = {
  src: string
  mimeType: string
  filename: string
}

type ItemResolver = (index: number) => Promise<PreviewItem> | PreviewItem

const props = defineProps({
  fullscreen: {
    type: Boolean,
    default: false,
  },
})

const showModel = ref(false)

const showAsFullscreen = ref(false)
syncRefs(() => props.fullscreen, showAsFullscreen)

const fileSrc = ref('')
const fileMimeType = ref('')
const fileName = ref('')

const currentIndex = ref(0)
const initialPage = ref<number>()
const totalCount = ref(0)
const isNavigatingPrev = ref(false)
const isNavigatingNext = ref(false)
const resolveItem = shallowRef<ItemResolver | null>(null)
const isLoadingItem = ref(false)
// the dialog is shared app-wide, so a deferred load must not touch it once another preview took over
let requestId = 0

function startRequest() {
  isLoadingItem.value = false
  return ++requestId
}

function resetNavigation() {
  resolveItem.value = null
  currentIndex.value = 0
  initialPage.value = undefined
  totalCount.value = 0
  isNavigatingPrev.value = false
  isNavigatingNext.value = false
}

function close() {
  resetNavigation()
}

function waitUntilClosed() {
  return until(showModel).toBe(false)
}

async function goTo(index: number, loading: Ref<boolean> = ref(false)) {
  if (!resolveItem.value) return
  if (index !== currentIndex.value) initialPage.value = undefined
  loading.value = true
  try {
    const item = await resolveItem.value(index)
    currentIndex.value = index
    fileSrc.value = item.src
    fileMimeType.value = item.mimeType
    fileName.value = item.filename
  } finally {
    loading.value = false
  }
}

function isNavigating() {
  return isNavigatingPrev.value || isNavigatingNext.value
}

function next() {
  if (isNavigating()) return
  goTo((currentIndex.value + 1) % totalCount.value, isNavigatingNext)
}

function prev() {
  if (isNavigating()) return
  goTo(
    (currentIndex.value - 1 + totalCount.value) % totalCount.value,
    isNavigatingPrev,
  )
}

function onKeydown(event: KeyboardEvent) {
  if (totalCount.value <= 1) return
  if (event.key === 'ArrowRight') next()
  else if (event.key === 'ArrowLeft') prev()
}

function showModal(
  src: string,
  mimeType = 'application/octet-stream',
  filename = src,
) {
  startRequest()
  resetNavigation()
  fileSrc.value = src
  fileMimeType.value = mimeType
  fileName.value = filename
  showModel.value = true
  return waitUntilClosed()
}

async function showModalWithNavigation(
  itemResolver: ItemResolver,
  initialIndex: number,
  count: number,
  page?: number,
) {
  startRequest()
  resetNavigation()
  resolveItem.value = itemResolver
  totalCount.value = count
  currentIndex.value = initialIndex
  initialPage.value = page
  await goTo(initialIndex)
  showModel.value = true
  return waitUntilClosed()
}

async function showModalDeferred(
  loadSrc: () => Promise<string>,
  mimeType: string,
  filename: string,
) {
  const id = startRequest()
  const isCurrent = () => id === requestId
  resetNavigation()
  fileSrc.value = ''
  fileMimeType.value = mimeType
  fileName.value = filename
  isLoadingItem.value = true
  showModel.value = true
  const closed = waitUntilClosed()
  try {
    const src = await loadSrc()
    if (isCurrent() && showModel.value) fileSrc.value = src
  } catch (e) {
    if (isCurrent()) showModel.value = false
    throw e
  } finally {
    if (isCurrent()) isLoadingItem.value = false
  }
  return closed
}

defineExpose({ showModal, showModalWithNavigation, showModalDeferred })
</script>
